/**
 * useUserAddons — single source of truth for "does this coach have a
 * Pro upsell active?". Components downstream gate features off this
 * instead of inventing their own subscription checks.
 *
 * Fetches the current user's `user_addons` rows on mount, exposes a
 * `has(addon_key)` helper, and refreshes when the auth state changes.
 *
 * Usage:
 *   const { has, loading, addons } = useUserAddons();
 *   if (!has("film_studio")) return <UpgradePrompt addonKey="film_studio"/>;
 *
 * Note on ground truth: the backing `user_addons` table is a mirror of
 * Stripe state, written by the stripe-webhook on subscription.updated.
 * If the user just toggled an addon, the webhook may take a few hundred
 * ms to fire; the manage-addon edge function does an optimistic upsert
 * so this hook usually sees the change immediately.
 */
import { useEffect, useState, useCallback } from "react";
import { supabase } from "../../supabaseClient";
import { FEATURE_TO_ADDON, isAllowedBetaUser, isAddonGatedToAllowlist } from "../constants/addons";

export function useUserAddons() {
  const [addons, setAddons] = useState([]); // [{addon_key, active, ...}]
  const [userEmail, setUserEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setAddons([]);
        setUserEmail(null);
        return;
      }
      setUserEmail(user.email || null);
      const { data, error } = await supabase
        .from("user_addons")
        .select("addon_key, active, started_at, ended_at, meta, stripe_subscription_item_id")
        .eq("user_id", user.id)
        .eq("active", true);
      if (error) throw error;
      setAddons(data || []);
    } catch (e) {
      console.warn("[useUserAddons] fetch failed:", e?.message);
      setError(e?.message || "Couldn't load add-ons");
      setAddons([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    // Re-fetch when auth state changes (sign in, sign out, token refresh).
    // Never awaited inside the auth callback: see onAuthStateChange in supabaseClient.js.
    const { data: sub } = supabase.auth.onAuthStateChange(() => { refresh(); });
    return () => sub?.subscription?.unsubscribe?.();
  }, [refresh]);

  // Does the user have this specific addon active?
  const has = useCallback((addonKey) => {
    if (!addonKey) return false;
    return addons.some(a => a.addon_key === addonKey && a.active);
  }, [addons]);

  // Does the user have access to a given feature? Maps the feature to
  // its required addon, then checks whether they have it. Components
  // downstream prefer this — they ask "can I show the upload button?"
  // not "is film_studio active?".
  const canUse = useCallback((featureKey) => {
    if (!featureKey) return false;
    const required = FEATURE_TO_ADDON[featureKey];
    if (!required) return true; // not gated by any addon
    return has(required);
  }, [has]);

  // Should we show this addon's marketing UI to the user at all?
  // Returns false for addons that are currently in private beta and
  // the user is not on the allowlist. The Add-ons settings panel uses
  // this to decide whether to render a card.
  const isVisible = useCallback((addonKey) => {
    if (!isAddonGatedToAllowlist(addonKey)) return true;
    return isAllowedBetaUser(userEmail);
  }, [userEmail]);

  return { addons, has, canUse, isVisible, userEmail, loading, error, refresh };
}

/**
 * useFeatureAccess — per-coach beta access check mirroring the server
 * rule in api/shotflow.js: allowed when the profile's addons or
 * allowlist_features include the feature, the user is an admin, or
 * their email is on the static beta allowlist. Lets Coleman grant a
 * coach with one SQL update (see the allowlist_features migration)
 * with no deploy.
 */
export function useFeatureAccess(feature) {
  const [state, setState] = useState({ loading: true, hasAccess: false });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { if (!cancelled) setState({ loading: false, hasAccess: false }); return; }
        if (isAllowedBetaUser(user.email)) { if (!cancelled) setState({ loading: false, hasAccess: true }); return; }
        const { data: p } = await supabase
          .from("profiles")
          .select("addons, allowlist_features, is_admin")
          .eq("id", user.id)
          .maybeSingle();
        const addons = Array.isArray(p?.addons) ? p.addons : [];
        const allow = Array.isArray(p?.allowlist_features) ? p.allowlist_features : [];
        const ok = p?.is_admin === true || addons.includes(feature) || allow.includes(feature);
        if (!cancelled) setState({ loading: false, hasAccess: ok });
      } catch {
        if (!cancelled) setState({ loading: false, hasAccess: false });
      }
    })();
    return () => { cancelled = true; };
  }, [feature]);
  return state;
}
